/**
 * Segment fill model + canonical CSS builders (WL-012, Fable Hail Mary Wave 2).
 *
 * ONE source of truth for the five Segment fill treatments the deck defines
 * (`concepts/segment-fills`): Color, Gradient, Pattern, Image, GIF. This is
 * the pure (no React / no DOM / no I/O) layer under `SegmentFillPanel` so the
 * exact string a Segment renders is testable and identical everywhere it is
 * used (Segment fill, wallpaper, block fill) - "everything is a shared
 * component".
 *
 * REUSE, NOT DUPLICATION (FEATURE-COMPLETENESS LAW):
 *  - Gradient CSS is delegated to the existing, secured, tested builder
 *    `components/gradientBuilder/gradientCss.js` (`buildGradientCss`).
 *  - Pattern SVG is delegated to `components/backgroundPatterns/patternGenerators.js`.
 *  This module adds ONLY what those lack for a *Segment* fill:
 *    (a) the conic seam-blend LOOP + centroid center rule
 *        (decisions-log row 36 + notes6) that the generic gradient builder
 *        does not implement, and
 *    (b) the GIF reduced-motion playback contract (a GIF never plays on the
 *        closed Wheel; poster frame + chip closed, animates only while open).
 *
 * The renderer's existing fill model is `{ type, ... }`; WL-054 renderer-v2
 * consumes the SAME shape, so this stays v2-compatible (see PR note).
 *
 * G-19 NOTE (this is NOT a hand-rolled Wheel): this module draws NO Wheel. It
 * only produces a CSS `background` string for ONE Segment's fill, which the
 * caller applies onto that Segment's own element inside the canonical Wheel
 * renderer (the `brand-wheel` / BRAND_WHEEL geometry drawn by BrandWheel /
 * D3Wheels). The centroid rule is measured against the component's own box; no
 * `<path>` arcs and no wheel SVG are emitted here.
 */

import {
  buildGradientCss,
  clampAngle,
  clampPosition,
} from "../../../../gradientBuilder/gradientCss";
import {
  generatePatternSvg,
  DEFAULT_FG,
  DEFAULT_BG,
} from "../../../../backgroundPatterns/patternGenerators";
import { svgToDataUri } from "../../../../backgroundPatterns/toBackgroundCss";

/** The five canonical Segment fill types (deck `segment-fills`). */
export const FILL_TYPES = ["color", "gradient", "pattern", "image", "gif"];

/** A GIF/Image "focus" crop anchor (state 08 cropper): normalised 0..1. */
export const DEFAULT_FOCUS = { x: 0.5, y: 0.5, zoom: 1 };

/** Canonical empty/default fill - a flat brand-neutral color. */
export const defaultSegmentFill = (color = "#6D5EF6") => ({
  type: "color",
  color,
});

const clamp01 = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0.5;
  return Math.min(1, Math.max(0, x));
};

const normFocus = (focus) => {
  const f = focus || {};
  return {
    x: clamp01(f.x ?? DEFAULT_FOCUS.x),
    y: clamp01(f.y ?? DEFAULT_FOCUS.y),
    zoom: Math.min(4, Math.max(1, Number(f.zoom) || 1)),
  };
};

/* -------------------------------------------------------------------------- */
/* Conic seam-blend loop (decisions-log row 36 + notes6, G14)                  */
/* -------------------------------------------------------------------------- */

/**
 * Build the LOOPED stop list for a Segment conic fill.
 *
 * The generic gradient builder emits the user's stops verbatim, which leaves a
 * hard 0%/100% seam on a conic sweep. Per notes6 the sweep must LOOP: the first
 * stop's color is appended at 100% and the user positions are rescaled so the
 * "return leg" gets one even leg's width - so the closing color blends back into
 * the opening color and no hard line exists anywhere on the wedge.
 *
 * Pure + deterministic: given N stops it returns N+1 stops, position 0 and
 * position 100 carrying the SAME color.
 *
 * @param {Array<{color:string,position:number}>} stops user stops (>=1)
 * @returns {Array<{color:string,position:number}>} looped stops (length N+1)
 */
export const buildConicLoopStops = (stops) => {
  const list = (Array.isArray(stops) ? stops.filter(Boolean) : []).map((s) => ({
    color: s.color,
    position: clampPosition(s.position),
  }));
  if (list.length === 0) return [{ color: "#000000", position: 0 }];
  if (list.length === 1) {
    // A single-color conic is already a flat loop - mirror it at both ends.
    return [
      { color: list[0].color, position: 0 },
      { color: list[0].color, position: 100 },
    ];
  }
  list.sort((a, b) => a.position - b.position);
  // One even leg reserved for the wrap back to the first color.
  const legWidth = 100 / list.length;
  const scale = (100 - legWidth) / 100;
  const rescaled = list.map((s) => ({
    color: s.color,
    position: Math.round(s.position * scale),
  }));
  rescaled.push({ color: list[0].color, position: 100 });
  return rescaled;
};

/**
 * Build the CSS for a Segment CONIC fill with the row-36 centroid rule.
 *
 * The gradient is centred at `50% 50%` of the element it is applied to - which,
 * per row 36, MUST be the Segment/component's own box (its centroid), NEVER the
 * wheel hub. The caller applies the returned string to the Segment's own element
 * (an SVG `<foreignObject>` clipped to the wedge path, as the app already does).
 * `fromAngle` points the wrap at the wedge's inner edge.
 *
 * @param {object} gradient builder state ({ stops, fromAngle })
 * @param {{loop?:boolean, center?:string}} [opts]
 * @returns {string} a valid conic gradient string
 */
export const buildSegmentConicCss = (gradient, opts = {}) => {
  const g = gradient || {};
  const loop = opts.loop !== false; // default ON (seam-blend)
  const center = opts.center || "50% 50%"; // centroid of the applied component
  const fromAngle = clampAngle(g.fromAngle);
  const stops = loop ? buildConicLoopStops(g.stops) : g.stops;
  // Delegate the actual serialisation (+ its color-injection guards) to the
  // shared builder by handing it the looped stops.
  return buildGradientCss({
    type: "conic",
    fromAngle,
    position: "center",
    stops,
  }).replace("at center", `at ${center}`);
};

/* -------------------------------------------------------------------------- */
/* GIF reduced-motion playback contract (segment-fills index.md, round 4)      */
/* -------------------------------------------------------------------------- */

/**
 * Resolve what a GIF fill should show given the Segment open/closed state and
 * the viewer's reduced-motion preference. A GIF NEVER animates on the closed
 * Wheel; it plays (on the arc AND in the panel header) only while the Segment
 * is open - and reduced-motion keeps the poster frame in every case.
 *
 * @param {{poster?:string,url?:string}} gif
 * @param {{open?:boolean, reducedMotion?:boolean}} state
 * @returns {{ animate:boolean, src:string, showChip:boolean }}
 */
export const resolveGifPlayback = (gif, state = {}) => {
  const g = gif || {};
  const poster = g.poster || g.url || "";
  const open = !!state.open;
  const reducedMotion = !!state.reducedMotion;
  const animate = open && !reducedMotion;
  return {
    animate,
    src: animate ? g.url || poster : poster,
    showChip: !open, // corner "GIF" chip only while closed
  };
};

/* -------------------------------------------------------------------------- */
/* The one entry point: fill -> CSS `background` value                          */
/* -------------------------------------------------------------------------- */

/**
 * Build the CSS `background` value for any Segment fill.
 *
 * @param {object} fill { type, color?, gradient?, pattern?, image?, gif? }
 * @param {{open?:boolean, reducedMotion?:boolean, center?:string}} [ctx]
 *        render context (GIF playback + conic centroid).
 * @returns {string} a CSS `background` value (color / gradient / url()).
 */
export const buildSegmentFillCss = (fill, ctx = {}) => {
  const f = fill || defaultSegmentFill();
  const type = FILL_TYPES.includes(f.type) ? f.type : "color";

  if (type === "color") {
    return f.color || "#6D5EF6";
  }

  if (type === "gradient") {
    const g = f.gradient || {};
    if ((g.type || "linear") === "conic") {
      return buildSegmentConicCss(g, { center: ctx.center, loop: g.loop });
    }
    return buildGradientCss(g);
  }

  if (type === "pattern") {
    const p = f.pattern || {};
    const svg = generatePatternSvg(p.id, {
      fg: p.fg || DEFAULT_FG,
      bg: p.bg || DEFAULT_BG,
      scale: p.scale,
      opacity: p.opacity,
    });
    // `generatePatternSvg` returns RAW `<svg …>` MARKUP — it is NOT a data-URI
    // and NOT safe to interpolate into `url("…")`: the markup's own double
    // quotes terminate the url string early, the CSS parser rejects the whole
    // declaration, and the segment paints a broken tile. Route it through the
    // shared `svgToDataUri` encoder (the same one the wallpaper tile and the
    // wedge `<image href>` use) so all three surfaces emit an identical,
    // parseable data-URI.
    const uri = svgToDataUri(svg);
    if (uri) return `url("${uri}")`;
    // Unknown/absent pattern id: a legacy fill carries the already-built css
    // string instead of a descriptor — use it rather than emitting an empty
    // `url("")`, which paints nothing.
    if (typeof p.css === "string" && p.css) return p.css;
    return p.bg || DEFAULT_BG;
  }

  if (type === "image") {
    const img = f.image || {};
    const focus = normFocus(img.focus);
    if (!img.url) return "#EDE7FB"; // empty placeholder
    return `url("${img.url}") ${Math.round(focus.x * 100)}% ${Math.round(
      focus.y * 100
    )}% / cover no-repeat`;
  }

  if (type === "gif") {
    const play = resolveGifPlayback(f.gif, ctx);
    const focus = normFocus((f.gif || {}).focus);
    if (!play.src) return "#EDE7FB";
    return `url("${play.src}") ${Math.round(focus.x * 100)}% ${Math.round(
      focus.y * 100
    )}% / cover no-repeat`;
  }

  return f.color || "#6D5EF6";
};

/** Human label for a fill type (UI + a11y). */
export const FILL_TYPE_LABEL = {
  color: "Color",
  gradient: "Gradient",
  pattern: "Pattern",
  image: "Image",
  gif: "GIF",
};
